import { DoseSpotPharmacy, Indication, MODULE_TYPE, Pharmacy, SurveyCategory } from '@abiglobalhealth/go-types'
import { createContext, Dispatch, PropsWithChildren, SetStateAction, useState } from 'react'

import { getInitialModule } from '@/api/modules'
import { resetTraces } from '@/api/traces'
import { ExitLogicModal } from '@/components/ExitLogicModal'
import { useConsultationType } from '@/hooks/useConsultationType'
import { useLoading } from '@/hooks/useLoading'
import { useModule } from '@/hooks/useModule'
import { useTraces } from '@/hooks/useTraces'
import { useUser } from '@/hooks/useUser'
import { formatTrace } from '@/utils/traces'

export interface SurveysContextProps {
  surveyCategories: SurveyCategory[]
  currentSurveyCategory: SurveyCategory | null
  setCurrentSurveyCategory: Dispatch<SetStateAction<SurveyCategory | null>>
  setShowExitLogicModal: Dispatch<SetStateAction<boolean>>
  indications: Indication[]
  setIndications: Dispatch<SetStateAction<Indication[]>>
  selectedPharmacy: Pharmacy | DoseSpotPharmacy | null
  setSelectedPharmacy: Dispatch<SetStateAction<Pharmacy | DoseSpotPharmacy | null>>
  handleExit: () => Promise<void>
}

export const SurveysContext = createContext({} as SurveysContextProps)

export const SurveysProvider = ({ children }: PropsWithChildren) => {
  const { currentModule, updateModule } = useModule()

  const [currentSurveyCategory, setCurrentSurveyCategory] = useState<SurveyCategory | null>(null)
  const [showExitLogicModal, setShowExitLogicModal] = useState<boolean>(false)
  const [indications, setIndications] = useState<Indication[]>([])
  const [selectedPharmacy, setSelectedPharmacy] = useState<Pharmacy | DoseSpotPharmacy | null>(null)
  const { user } = useUser()
  const { consultationType } = useConsultationType()
  const { showLoading, hideLoading } = useLoading()
  const { setTraces } = useTraces()

  const handleExit = async () => {
    showLoading()

    await resetTraces({
      uniqueId: user?.data?.uniqueId!
    })

    const { module } = await getInitialModule({
      uniqueId: user?.data?.uniqueId!,
      userId: user?.data?.userId!,
      serviceType: consultationType
    })
    updateModule(module)

    setCurrentSurveyCategory(null)
    setIndications([])
    setSelectedPharmacy(null)
    setShowExitLogicModal(false) // Hide modal
    setTraces([formatTrace(module.id)]) // Reset traces

    hideLoading()
  }

  const handleUndo = () => {
    setShowExitLogicModal(false) // Hide modal
  }

  const getSurveyCategories = (): SurveyCategory[] => {
    if (!currentModule) return []

    if (currentModule.type === MODULE_TYPE.INDICATION_CATEGORIES) {
      return currentModule.indicationCategories.map((category) => ({
        id: category.id,
        textKey: category.textKey,
        iconUrl: category.iconUrl,
        options: category.indications
      }))
    }

    if (currentModule.type === MODULE_TYPE.ASSESSMENT_SURVEY_CATEGORIES) {
      return currentModule.assessmentSurveyCategories.map((category) => ({
        id: category.id,
        textKey: category.textKey,
        iconUrl: category.iconUrl,
        options: category.assessments
      }))
    }

    return []
  }

  const value: SurveysContextProps = {
    surveyCategories: getSurveyCategories(),
    currentSurveyCategory,
    setCurrentSurveyCategory,
    setShowExitLogicModal,
    indications,
    setIndications,
    selectedPharmacy,
    setSelectedPharmacy,
    handleExit
  }

  return (
    <>
      {showExitLogicModal && <ExitLogicModal handleUndo={handleUndo} handleEnd={handleExit} />}
      <SurveysContext.Provider value={value}>{children}</SurveysContext.Provider>
    </>
  )
}
